// 📋 Surveys (Phase D3b) — the staff survey list + form + PIN-attributed
// submit (index.html tab). ToyTalk "Feedback" replacement, answering surface.
//
// Product rules:
//   - The list shows OPEN surveys targeting THIS person (all / country /
//     stores / named individuals — the Worker decides; deadline shown).
//   - Answers are NAMED: submitting needs the PIN (memory only, 5-min idle —
//     the chat A3 posture; the Worker attributes from the VERIFIED row).
//     One response per person; re-submitting before the deadline updates it.
//   - The who-record is SHARED with chat/news/tasks ('chat_who_v1').
//   - Accounts see nothing here (staff-only surface; oversight lives in the
//     admin app's results board).
//
// Bare-name globals (currentScreen, currentStore, currentUser, getStoreStaff,
// DB) are typeof-guarded — the classic-script bundle contract.

// v129.116 — reference files on a survey (Matt, 13 Aug). This file had NO
// imports before now; a test asserts the BUILT dist/surveys.js carries the
// shared code, because an import that fails to apply leaves an undefined global
// and a source-scanning test cannot tell the difference (the v129.110 lesson).
import { attachLinksHtml, openFromPayload } from '../shared/attachments-ui.js';

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function initials(name) {
  var p = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
function isAccountMode() {
  try {
    return !!(typeof currentUser !== 'undefined' && currentUser &&
      ['admin', 'area_manager', 'ho_staff'].indexOf(String(currentUser.role)) !== -1 &&
      typeof DB !== 'undefined' && DB.getToken && DB.getToken());
  } catch (e) { return false; }
}

// ── State (the PIN is deliberately NOT persisted anywhere) ────────────────────
var _who = null;                 // { id, name } — SHARED key 'chat_who_v1'
var _pin = null;                 // in-memory only
var _pinAt = 0;
var PIN_IDLE_MS = 5 * 60 * 1000;
var _surveys = [];
var _q = '';                     // v126.70: staff search across survey title + intro
var _scope = 'all';              // v126.70: 'all' | 'me' (me = TO DO — not yet answered)
var _openId = null;              // survey being answered
var _pendingAnswers = {};        // v125.16 (review E): survey id → answers kept after a failed submit (re-render/nav safe)
var _submitting = false;

function loadWho() {
  if (_who) return _who;
  try {
    var raw = localStorage.getItem('chat_who_v1');
    if (raw) { var w = JSON.parse(raw); if (w && w.id && w.name) _who = { id: w.id, name: w.name }; }
  } catch (e) { /* fresh pick */ }
  return _who;
}
function saveWho(w) {
  _who = w ? { id: w.id, name: w.name } : null;  // id + name ONLY — never a PIN
  try {
    if (_who) localStorage.setItem('chat_who_v1', JSON.stringify(_who));
    else localStorage.removeItem('chat_who_v1');
  } catch (e) { /* storage unavailable — session-only */ }
}
function pinFresh() { return _pin && (Date.now() - _pinAt) < PIN_IDLE_MS; }
function clearPin() { _pin = null; _pinAt = 0; }

function root() { return document.getElementById('surveysRoot'); }

var _styled = false;
function ensureStyles() {
  if (_styled) return;
  _styled = true;
  var css = '' +
    '#surveysRoot .sv-shell{position:relative;max-width:680px;margin:0 auto;background:var(--bg,#f0f2f5);border-radius:12px;overflow:hidden;display:flex;flex-direction:column;min-height:60vh}' +
    '#surveysRoot .sv-head{background:var(--brand,#009E3D);color:#fff;padding:12px 16px;display:flex;align-items:center;gap:10px}' +
    '#surveysRoot .sv-hname{font-weight:700;font-size:16px}' +
    '#surveysRoot .sv-hsub{font-size:11.5px;opacity:.85}' +
    '#surveysRoot .sv-feed{padding:12px;display:flex;flex-direction:column;gap:10px}' +
    '#surveysRoot .sv-card{background:var(--card);border-radius:10px;padding:12px 14px;box-shadow:0 1px 1px rgba(0,0,0,.08)}' +
    '#surveysRoot .sv-card.sv-todo{border-inline-start:4px solid var(--brand,#009E3D)}' +
    '#surveysRoot .sv-title{font-weight:700;font-size:15px;color:var(--text);display:flex;align-items:center;gap:6px;flex-wrap:wrap}' +
    '#surveysRoot .sv-chip{font-size:10px;font-weight:800;background:var(--brand,#009E3D);color:#fff;border-radius:8px;padding:1px 7px}' +
    '#surveysRoot .sv-meta{font-size:11px;color:var(--tiny);margin-top:6px}' +
    '#surveysRoot .sv-q{margin-top:12px;font-size:13.5px;color:var(--text);font-weight:600}' +
    '#surveysRoot .sv-opt{display:block;margin-top:6px;font-size:13px;color:var(--text)}' +
    '#surveysRoot .sv-txt{width:100%;min-height:70px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px;margin-top:6px;box-sizing:border-box;font-family:inherit}' +
    '#surveysRoot .sv-star{font-size:24px;cursor:pointer;opacity:.35;margin-inline-end:4px}' +
    '#surveysRoot .sv-star.on{opacity:1}' +
    '#surveysRoot .sv-go{margin-top:12px;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:8px;padding:9px 16px;font-weight:700;font-size:13.5px;cursor:pointer}' +
    '#surveysRoot .sv-back{margin-top:8px;background:none;border:none;color:var(--sub);font-size:12.5px;cursor:pointer}' +
    '#surveysRoot .sv-done{margin-top:8px;color:var(--brand-dark,#007A2F);font-weight:700;font-size:13px}' +
    '#surveysRoot .sv-empty{padding:28px;text-align:center;color:var(--sub);font-size:13.5px}' +
    '#surveysRoot .sv-notice{background:var(--warn-bg);color:var(--warn-deep);font-size:12px;padding:8px 12px;text-align:center}' +
    '#surveysRoot .sv-pick{background:var(--card);max-height:50vh;overflow-y:auto}' +
    '#surveysRoot .sv-item{display:flex;gap:10px;padding:10px 14px;border-bottom:1px solid var(--border);cursor:pointer;align-items:center}' +
    '#surveysRoot .sv-iav{width:36px;height:36px;border-radius:50%;background:var(--brand,#009E3D);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;flex:0 0 36px}' +
    '#surveysRoot .sv-modal{position:fixed;inset:0;background:rgba(11,20,26,.6);display:flex;align-items:center;justify-content:center;z-index:3000}' +
    '#surveysRoot .sv-mcard{background:var(--card);border-radius:12px;padding:20px;width:min(340px,90vw);text-align:center}' +
    '#surveysRoot .sv-mpin{width:100%;font-size:22px;letter-spacing:8px;text-align:center;padding:10px;border:2px solid var(--text);border-radius:10px;margin:14px 0}' +
    '#surveysRoot .sv-mbtn{width:100%;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:10px;padding:11px;font-weight:700;font-size:14px;cursor:pointer}' +
    '#surveysRoot .sv-mcancel{margin-top:10px;color:var(--sub);background:none;border:none;font-size:13px;cursor:pointer}';
  var el = document.createElement('style');
  el.id = 'surveysStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

// ── Screen entry point (called by switchScreen('surveys')) ───────────────────
function renderSurveysScreen() {
  ensureStyles();
  if (isAccountMode()) { renderAccountNote(); return; }
  if (!loadWho()) { renderWhoPicker(); return; }
  if (_openId) renderForm(); else renderList();
  refreshSurveys();
}

function renderAccountNote() {
  var el = root(); if (!el) return;
  el.innerHTML = '<div class="sv-shell"><div class="sv-head"><div style="font-size:22px">📋</div>' +
    '<div><div class="sv-hname">Surveys</div><div class="sv-hsub">Staff answer here</div></div></div>' +
    '<div class="sv-empty">Surveys are answered by store staff. Results and completion live in the manager app\'s 📋 Surveys tab.</div></div>';
}

function renderWhoPicker() {
  var el = root(); if (!el) return;
  var staff = [];
  // v127.42 (a store manager's report #2, task-close picker 29 Jul): the identity picker shows
  // home staff + TODAY'S ROSTERED COVER (getStoreRoster's v113.0 second arg),
  // so a supporter transferred to another store can act under their own name
  // at the store they're covering — the same rule as clock/checks/tally.
  // Falls back to home-only where the roster helper isn't loaded (chat.html).
  try {
    if (typeof currentStore !== 'undefined') {
      var _rosterDay = (typeof businessDayStr === 'function') ? businessDayStr() : null;
      if (typeof getStoreRoster === 'function') staff = getStoreRoster(currentStore, _rosterDay) || [];
      else if (typeof getStoreStaff !== 'undefined') staff = getStoreStaff(currentStore) || [];
    }
  } catch (e) { staff = []; }
  var rows = staff.map(function (s) {
    return '<div class="sv-item" onclick="Surveys.pickWho(' + s.id + ',\'' + esc(String(s.name || '').replace(/'/g, '')) + '\')">' +
      '<div class="sv-iav">' + esc(initials(s.name)) + '</div>' +
      '<div><div translate="no" style="font-weight:600;font-size:14px;color:var(--text)">' + esc(s.name) + '</div>' +
      '<div style="font-size:12px;color:var(--tiny)">' + esc(s.role || '') + '</div></div></div>';
  }).join('');
  el.innerHTML = '<div class="sv-shell">' +
    '<div class="sv-head"><div style="font-size:22px">📋</div><div><div class="sv-hname">Surveys</div><div class="sv-hsub">Who are you?</div></div></div>' +
    '<div class="sv-notice">Pick your name to see surveys for you. You\'ll only need your PIN when you submit.</div>' +
    '<div class="sv-pick">' + (rows || '<div class="sv-empty">No staff found for this store yet.</div>') + '</div></div>';
}
function pickWho(id, name) {
  saveWho({ id: id, name: name });
  clearPin();
  renderSurveysScreen();
}
function switchWho() { saveWho(null); clearPin(); _openId = null; renderSurveysScreen(); }

// ── The list ──────────────────────────────────────────────────────────────────
async function refreshSurveys() {
  if (!_who || isAccountMode()) return;
  try {
    var res = await api('/api/surveys?staff_id=' + encodeURIComponent(_who.id));
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _surveys = d.surveys || [];
      updateNavBadge();
      if ((typeof currentScreen === 'undefined' || currentScreen === 'surveys') && !document.hidden && !_openId) renderList();
    }
  } catch (e) { /* poll again later */ }
}

function updateNavBadge() {
  var open = 0;
  _surveys.forEach(function (s) { if (!s.responded) open++; });
  var nav = document.getElementById('nav-surveys');
  if (!nav) return;
  var b = document.getElementById('surveysNavBadge');
  if (open > 0) {
    if (!b) {
      b = document.createElement('span');
      b.id = 'surveysNavBadge';
      b.style.cssText = 'position:absolute;top:2px;inset-inline-end:14px;min-width:16px;height:16px;border-radius:8px;background:var(--brand,#009E3D);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px';
      nav.style.position = 'relative';
      nav.appendChild(b);
    }
    b.textContent = open > 99 ? '99+' : String(open);
  } else if (b) b.remove();
}

// v126.70 (Head Office: "search easy... surveys too") — the same compact bar as Tasks
// & News: 🔍 search across title + intro, and an All | Me toggle where "Me" =
// still TO DO (not yet answered). Re-render keeps the search caret at the end.
function matchesFilter(s) {
  if (_scope === 'me' && s.responded) return false;
  var q = _q.trim().toLowerCase();
  if (!q) return true;
  return ((String(s.title || '') + ' ' + String(s.intro || '')).toLowerCase().indexOf(q) !== -1);
}
function filterBarHtml() {
  var seg = function (val, label) {
    var on = _scope === val;
    return '<span onclick="Surveys.setScope(\'' + val + '\')" style="cursor:pointer;padding:5px 13px;border-radius:14px;font-size:12px;font-weight:700;' +
      (on ? 'background:var(--brand,#009E3D);color:#fff' : 'background:var(--surface-3);color:var(--sub)') + '">' + label + '</span>';
  };
  return '<div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;flex-wrap:wrap">' +
    '<input id="svSearch" value="' + esc(_q) + '" oninput="Surveys.onSearch(this.value)" placeholder="🔍 Search surveys…" ' +
    'style="flex:1;min-width:150px;padding:8px 12px;border:1px solid var(--border-strong);border-radius:10px;font-size:13px;box-sizing:border-box">' +
    '<div style="display:flex;gap:6px">' + seg('all', 'All') + seg('me', 'Me') + '</div></div>';
}
function onSearch(v) {
  _q = String(v == null ? '' : v);
  renderList();
  var i = document.getElementById('svSearch');
  if (i) { try { i.focus(); i.setSelectionRange(i.value.length, i.value.length); } catch (e) {} }
}
function setScope(s) { _scope = (s === 'me') ? 'me' : 'all'; renderList(); }

function renderList() {
  var el = root(); if (!el) return;
  var searching = _q.trim() !== '';
  var cards = _surveys.filter(matchesFilter).map(function (s) {
    return '<div class="sv-card' + (!s.responded ? ' sv-todo' : '') + '">' +
      '<div class="sv-title">' + (!s.responded ? '<span class="sv-chip">TO DO</span>' : '') + '<span translate="no">' + esc(s.title) + '</span></div>' +
      '<div class="sv-meta">Deadline ' + esc(s.deadline) + ' · ' + esc(s.created_by || 'Head Office') + '</div>' +
      // v129.116 — the reference file(s) Head Office attached, one link each.
      attachLinksHtml(s, 'Surveys.openAttachment', { className: 'sv-meta', prefix: 'View ' }) +
      (s.responded
        ? '<div class="sv-done">✓ Submitted — you can update it until the deadline</div>' +
          '<button class="sv-back" onclick="Surveys.openSurvey(' + s.id + ')">Edit my answers</button>'
        : '<button class="sv-go" onclick="Surveys.openSurvey(' + s.id + ')">Answer now</button>') +
      '</div>';
  }).join('');
  el.innerHTML = '<div class="sv-shell">' +
    '<div class="sv-head"><div style="font-size:22px">📋</div><div><div class="sv-hname">Surveys</div>' +
    '<div class="sv-hsub">🔒 Answers go to Head Office with your name</div></div>' +
    (_who ? '<div style="margin-inline-start:auto;font-size:11.5px;background:rgba(255,255,255,.15);border-radius:12px;padding:4px 10px;cursor:pointer" onclick="Surveys.switchWho()">' + esc(_who.name) + ' · switch</div>' : '') +
    '</div>' +
    '<div class="sv-feed">' + filterBarHtml() +
    (cards || '<div class="sv-empty">' + (searching
      ? 'No surveys match "' + esc(_q.trim()) + '".'
      : (_scope === 'me' ? 'All caught up — nothing to answer. 🎉' : 'No surveys for you right now. 🎉')) + '</div>') +
    '</div></div>';
}

// ── The form ──────────────────────────────────────────────────────────────────
function openSurvey(id) { _openId = id; renderForm(); }
function closeSurvey() { _openId = null; renderList(); }

function renderForm() {
  var el = root(); if (!el) return;
  var s = _surveys.find(function (x) { return x.id === _openId; });
  if (!s) { closeSurvey(); return; }
  // v125.16 (review E): if a submit failed on a flaky connection, the typed/
  // picked answers are kept in memory and re-applied here, so a re-render or a
  // tab switch doesn't wipe them. Only lost on a full app reload.
  var pend = _pendingAnswers[_openId] || {};
  var qs = (s.questions || []).map(function (q) {
    var pv = pend[q.id];
    var input = '';
    if (q.type === 'choice') {
      input = (q.options || []).map(function (o) {
        return '<label class="sv-opt"><input type="radio" name="svQ' + q.id + '" value="' + esc(o) + '"' + (String(pv) === String(o) ? ' checked' : '') + '> <span translate="no">' + esc(o) + '</span></label>';
      }).join('');
    } else if (q.type === 'yesno') {
      input = '<label class="sv-opt"><input type="radio" name="svQ' + q.id + '" value="yes"' + (pv === 'yes' ? ' checked' : '') + '> Yes</label>' +
        '<label class="sv-opt"><input type="radio" name="svQ' + q.id + '" value="no"' + (pv === 'no' ? ' checked' : '') + '> No</label>';
    } else if (q.type === 'rating') {
      var rv = Number(pv) || 0;
      input = '<div id="svR' + q.id + '" data-val="' + (rv || '') + '">' + [1, 2, 3, 4, 5].map(function (n) {
        return '<span class="sv-star' + (rv && n <= rv ? ' on' : '') + '" data-n="' + n + '" onclick="Surveys.rate(' + q.id + ',' + n + ')">⭐</span>';
      }).join('') + '</div>';
    } else {
      input = '<textarea class="sv-txt" id="svT' + q.id + '" maxlength="1000" placeholder="Type your answer…">' + esc(pv != null ? String(pv) : '') + '</textarea>';
    }
    return '<div class="sv-q"><span translate="no">' + esc(q.label) + '</span>' + (q.required !== false ? ' *' : '') + '</div>' + input;
  }).join('');
  el.innerHTML = '<div class="sv-shell">' +
    '<div class="sv-head"><button class="sv-back" style="color:#fff;font-size:18px;margin:0" onclick="Surveys.closeSurvey()">←</button>' +
    '<div><div class="sv-hname" translate="no">' + esc(s.title) + '</div><div class="sv-hsub">Deadline ' + esc(s.deadline) + ' · answers carry your name</div></div></div>' +
    '<div class="sv-feed"><div class="sv-card">' +
    (s.intro ? '<div translate="no" style="font-size:13px;color:var(--text);white-space:pre-wrap">' + esc(s.intro) + '</div>' : '') +
    // v129.116 — the file is needed HERE too, not just on the list card: if the
    // survey asks about a planogram, the planogram has to be reachable while
    // answering, without backing out and losing the form.
    attachLinksHtml(s, 'Surveys.openAttachment', { className: 'sv-meta', prefix: 'View ' }) +
    qs +
    '<button class="sv-go" onclick="Surveys.submit()">Submit my answers</button>' +
    '</div></div></div>';
}

// v129.116 — open reference file `idx` of a survey. `staff_id` is required by
// the Worker (a survey audience can be individual PEOPLE, so a store name is not
// enough to decide who may read it) — so this needs a picked name, exactly like
// answering does.
async function openAttachment(surveyId, idx) {
  var warn = function (m) { if (typeof showToast !== 'undefined') showToast(m, 'error'); };
  var who = loadWho();
  if (!who) { warn('Pick your name first'); return; }
  try {
    var res = await api('/api/surveys/attachment?id=' + encodeURIComponent(surveyId)
      + '&staff_id=' + encodeURIComponent(who.id));
    if (!res.ok) { warn('Could not open the file'); return; }
    var d = await res.json();
    if (!d || !d.ok || !openFromPayload(d, Number(idx) || 0)) warn('Could not open the file');
  } catch (e) { warn('Could not open the file'); }
}

function rate(qid, n) {
  var box = document.getElementById('svR' + qid);
  if (!box) return;
  box.setAttribute('data-val', String(n));
  box.querySelectorAll('.sv-star').forEach(function (st) {
    st.classList.toggle('on', Number(st.getAttribute('data-n')) <= n);
  });
}

function collectAnswers(s) {
  var out = {};
  (s.questions || []).forEach(function (q) {
    if (q.type === 'choice' || q.type === 'yesno') {
      var sel = document.querySelector('input[name="svQ' + q.id + '"]:checked');
      if (sel) out[q.id] = sel.value;
    } else if (q.type === 'rating') {
      var box = document.getElementById('svR' + q.id);
      var v = box ? box.getAttribute('data-val') : '';
      if (v) out[q.id] = Number(v);
    } else {
      var t = document.getElementById('svT' + q.id);
      var txt = t ? String(t.value || '').trim() : '';
      if (txt) out[q.id] = txt;
    }
  });
  return out;
}

function submit() {
  var s = _surveys.find(function (x) { return x.id === _openId; });
  if (!s) return;
  var answers = collectAnswers(s);
  var missing = (s.questions || []).find(function (q) { return q.required !== false && (answers[q.id] == null || answers[q.id] === ''); });
  if (missing) {
    if (typeof showToast !== 'undefined') showToast('Please answer: ' + missing.label, 'error');
    return;
  }
  if (pinFresh()) { doSubmit(answers); return; }
  askPin(function () { doSubmit(answers); });
}

async function doSubmit(answers) {
  if (_submitting) return;
  _submitting = true;
  try {
    var res = await api('/api/surveys/submit', {
      method: 'POST',
      body: JSON.stringify({ survey_id: _openId, answers: answers, auth_staff_id: _who.id, auth_pin: _pin }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _pinAt = Date.now();
      var s = _surveys.find(function (x) { return x.id === _openId; });
      if (s) s.responded = true;
      delete _pendingAnswers[_openId];    // v125.16 (E): saved — drop the kept copy
      _openId = null;
      updateNavBadge();
      renderList();
      if (typeof showToast !== 'undefined') showToast('Thanks — your answers are in!');
    } else {
      clearPin();
      if (res.status === 401 || res.status === 429) askPin(function () { doSubmit(answers); }, (d && d.error) || 'PIN check failed — try again');
      else { _pendingAnswers[_openId] = answers; if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not submit — your answers are kept, try again', 'error'); }
    }
  } catch (e) {
    _pendingAnswers[_openId] = answers;   // v125.16 (E): offline — keep the answers so a re-render doesn't wipe them
    if (typeof showToast !== 'undefined') showToast('No connection — your answers are kept, try again', 'error');
  } finally {
    _submitting = false;
  }
}

// The PIN prompt — memory only, 5-min idle expiry (chat A3 posture).
function askPin(then, errMsg) {
  var el = root(); if (!el) return;
  var old = document.getElementById('svPinModal');
  if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'svPinModal';
  wrap.className = 'sv-modal';
  wrap.innerHTML = '<div class="sv-mcard">' +
    '<div style="font-weight:700;font-size:15px;color:var(--text)">' + esc(_who ? _who.name : '') + ' — confirm it\'s you</div>' +
    '<div style="font-size:12.5px;color:var(--sub);margin-top:6px">' + (errMsg ? esc(errMsg) : 'Enter your till PIN to submit in your name.') + '</div>' +
    '<input id="svPinInput" class="sv-mpin" type="password" inputmode="numeric" autocomplete="off" maxlength="8">' +
    '<button class="sv-mbtn" id="svPinGo">Confirm</button>' +
    '<button class="sv-mcancel" id="svPinCancel">Cancel</button></div>';
  el.appendChild(wrap);
  var input = document.getElementById('svPinInput');
  input.focus();
  function go() {
    var v = String(input.value || '').trim();
    if (!v) return;
    _pin = v; _pinAt = Date.now();
    wrap.remove();
    then();
  }
  document.getElementById('svPinGo').onclick = go;
  input.onkeydown = function (e) { if (e.key === 'Enter') go(); };
  document.getElementById('svPinCancel').onclick = function () { wrap.remove(); };
}

// ── Background badge (light: every 5 min, visible page + identity only) ──────
if (typeof window !== 'undefined') {
  setInterval(function () {
    if (document.hidden) return;
    if (!isAccountMode() && loadWho()) refreshSurveys();
  }, 5 * 60 * 1000);
}

// ── Window bindings (classic-script bundle contract) ─────────────────────────
if (typeof window !== 'undefined') {
  window.renderSurveysScreen = renderSurveysScreen;
}

// v125.14 CRITICAL: handlers are EXPORTED, not on `window.Surveys = {…}` —
// esbuild's `globalName:'Surveys'` assigns the module EXPORTS to window.Surveys,
// clobbering any in-body assignment, so an unexported handler = a dead
// `onclick="Surveys.openSurvey()"`. (See the news bundle for the full note.)
export {
  esc, initials, collectAnswers,
  pickWho, switchWho, openSurvey, closeSurvey, rate, submit,
  onSearch, setScope,                    // v126.70 — search + All/Me filter
  openAttachment,                        // v129.116 — open a survey's reference file
};
